/** @format */

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import FormattedText from "../formattedText/FormattedText";
import { useLocale } from "../i18n/hooks";
import { Subject } from "../resources/types";

import { ReactComponent as AsbwLogo } from "../../assets/images/asbwLogoBlack.svg";
import { ReactComponent as QDOIcon } from "../../assets/images/qdo/icon.svg";
import SubjectIcon from "../subjectIcon/SubjectIcon";

import stringsCy from "./locales/strings.cy.json";
import stringsEn from "./locales/strings.en.json";

type Props = {
  resourceTitle?: string;
  themeLevel?: string;
  subject?: Subject;
};

const PageHeader: React.FC<Props> = ({
  resourceTitle,
  themeLevel,
  subject,
}) => {
  const locale = useLocale();
  const { t: _, i18n } = useTranslation();

  i18n.addResources("cy", "translation", stringsCy);
  i18n.addResources("en", "translation", stringsEn);

  const icon = () => {
    switch (themeLevel) {
      case "asbwales":
        return (
          <Suspense fallback={<svg viewBox="0 0 200 40"></svg>}>
            <AsbwLogo style={{ width: "200" }} />
          </Suspense>
        );
      case "qdo":
        return (
          <div className="qdo-icon mb-2">
            <Suspense fallback={<svg viewBox="0 0 106 80"></svg>}>
              <QDOIcon style={{ width: "106px" }} />
            </Suspense>
          </div>
        );
      default:
        return subject ? (
          <div className="subject-icon">
            <Suspense fallback={<svg viewBox="0 0 200 40"></svg>}>
              <SubjectIcon subject={subject} />
            </Suspense>
          </div>
        ) : (
          <></>
        );
    }
  };

  const themeTag = () => {
    switch (themeLevel) {
      case "mfwGcse":
      case "mfwAlevel":
      case "mfwLevel2":
      case "qdo":
        return (
          <>
            {_("Made for Wales")}
            <br />
            {_("Ready for the world")}
          </>
        );

      case "multiLevel":
        return <>{_("VTQ & Skills")}</>;
      case "vq":
      case "vqEntryPath":
      case "vqLevel12":
      case "vqLevel23":
      case "vqLevel3":
      case "other":
        return <>{_("Vocational Qualification")}</>;

      case "alevel":
      case "gcse":
        return <>{_(themeLevel)}</>;
      default:
        <></>;
    }
  };

  return (
    <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
      <div className="page-label-start d-flex flex-column gap-2">
        <div className="icon">{icon()}</div>
        {themeLevel &&
          !["alevel", "gcse", "asbwales", "other", "qdo"].includes(
            themeLevel,
          ) && (
            <div
              className={`${
                ["vqEntryPath", "vqLevel12", "vqLevel23", "vqLevel3"].includes(
                  themeLevel,
                )
                  ? "levelWhite"
                  : "level"
              } d-none d-sm-block`}
            >
              <span className="fw-bolder d-block py-1 h5 mb-1">
                {themeLevel === "multiLevel" ? _("Multi Level") : _(themeLevel)}
              </span>
            </div>
          )}
      </div>
      <div className="page-title flex-grow-1">
        <h1 className="text-center text-sm-start fw-bold ms-2 mb-0">
          <FormattedText html={resourceTitle || ""} />
        </h1>
      </div>
      {themeLevel !== "asbwales" && (
        <div
          className={`page-label-end ${
            themeLevel &&
            [
              "alevel",
              "gcse",
              "vqEntryPath",
              "vqLevel12",
              "vqLevel23",
              "vqLevel3",
            ].includes(themeLevel)
              ? "tagWhite"
              : "tag"
          } flex-column gap-2 d-none d-sm-flex`}
        >
          <div className="d-block d-flex flex-column">
            <span className="fw-bolder h5 py-1 text-end mb-0 d-block">
              {themeTag()}
            </span>
          </div>

          {/* If legacy then level and subject */}
          {/* If mfw then mfw slogan and subject */}
          <div className="d-block">
            {themeLevel === "qdo" ? (
              <span className="fw-bolder py-1 h5 mb-1 text-end d-block">
                {_("Guidance")}
              </span>
            ) : (
              <>
                {subject && (
                  <span className="fw-bolder h5 py-1 mb-1 text-end d-block">
                    {subject.title[locale]}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeader;
